import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonSpinner,
  IonBadge,
  IonToast,
  IonAccordion,
  IonAccordionGroup,
  IonItem,
  IonLabel,
  IonModal,
  IonAlert,
  useIonViewWillEnter
} from '@ionic/react';
import {
  logOutOutline,
  shieldCheckmarkOutline,
  checkmarkCircle,
  closeCircle,
  trashOutline,
  closeOutline,
  playCircleOutline,
  personRemoveOutline,
  mailOutline,
  openOutline,
  locationOutline,
  chevronForwardOutline,
  calendarOutline
} from 'ionicons/icons';
import { onAuthStateChanged } from 'firebase/auth';
import { useHistory } from 'react-router-dom';
import { auth } from '../config/firebase';
import { signOut } from '../services/authService';
import {
  getPendingEvents,
  getApprovedEvents,
  deactivateEvent,
  isModeratorEmail,
  approveEvent,
  approveAndSetAwaitingToken,
  deleteEvent
} from '../services/moderatorService';
import { generateToken, saveToken } from '../services/tokenService';
import { sendTokenEmail } from '../services/emailService';
import {
  getPendingDeletionRequests,
  markDeletionAsCompleted,
  rejectDeletionRequest,
  DeletionRequest
} from '../services/deletionService';
import { Event } from '../types/event.types';
import { useTranslation } from '../hooks/useTranslation';
import ConfirmDialog from '../components/ConfirmDialog';
import './ModeratorPage.css';

interface MediaItem {
  type: 'image' | 'video';
  url: string;
  label: string;
}

const ModeratorPage: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [currentEmail, setCurrentEmail] = useState('');
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [approvedEvents, setApprovedEvents] = useState<Event[]>([]);
  const [detailEvent, setDetailEvent] = useState<Event | null>(null);
  const [detailType, setDetailType] = useState<'pending' | 'approved'>('pending');
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [deletionRequests, setDeletionRequests] = useState<DeletionRequest[]>([]);
  const [confirmDeletionCompleteId, setConfirmDeletionCompleteId] = useState<string | null>(null);
  const [confirmDeletionRejectId, setConfirmDeletionRejectId] = useState<string | null>(null);
  const [mapCoords, setMapCoords] = useState<{lat: number; lng: number; label: string} | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser?.email) {
        history.replace('/login');
        return;
      }
      setCurrentEmail(firebaseUser.email);
      const isMod = await isModeratorEmail(firebaseUser.email);
      setAuthorized(isMod);
      if (!isMod) {
        setLoading(false);
        return;
      }
      await loadEvents();
      await loadDeletionRequests();
    });
    return () => unsubscribe();
  }, []);

  useIonViewWillEnter(() => {
    if (authorized) {
      loadEvents();
      loadDeletionRequests();
    }
  });

  const loadEvents = async () => {
    setLoading(true);
    try {
      const [pending, approved] = await Promise.all([
        getPendingEvents(),
        getApprovedEvents(),
      ]);
      setEvents(pending);
      setApprovedEvents(approved);
    } catch {
      showFeedback(t('mod.error_load'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadDeletionRequests = async () => {
    try {
      const pending = await getPendingDeletionRequests();
      setDeletionRequests(pending);
    } catch {
      // silencioso: la sección de baja no bloquea la de eventos
    }
  };

  const handleCompleteDeletion = async (requestId: string) => {
    setConfirmDeletionCompleteId(null);
    setProcessingId(requestId);
    try {
      await markDeletionAsCompleted(requestId, currentEmail);
      setDeletionRequests(prev => prev.filter(r => r.id !== requestId));
      showFeedback(t('mod.deletion.completed_ok'), 'success');
    } catch {
      showFeedback(t('mod.deletion.error'), 'danger');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectDeletion = async (requestId: string) => {
    setConfirmDeletionRejectId(null);
    setProcessingId(requestId);
    try {
      await rejectDeletionRequest(requestId, currentEmail);
      setDeletionRequests(prev => prev.filter(r => r.id !== requestId));
      showFeedback(t('mod.deletion.rejected_ok'), 'success');
    } catch {
      showFeedback(t('mod.deletion.error'), 'danger');
    } finally {
      setProcessingId(null);
    }
  };

  const showFeedback = (message: string, color: 'success' | 'danger') => {
    setToastMessage(message);
    setToastColor(color);
    setShowToast(true);
  };

  const handleApprove = async (eventId: string) => {
    setProcessingId(eventId);
    try {
      await approveEvent(eventId);
      const approved = events.find(e => e.id === eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      if (approved) {
        setApprovedEvents(prev => [{ ...approved, status: 1, published: true }, ...prev]);
      }
      showFeedback(t('mod.approve_success'), 'success');
    } catch {
      showFeedback(t('mod.error_load'), 'danger');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveAndSendToken = async (event: Event) => {
    setProcessingId(event.id);
    try {
      const token = generateToken();
      const email = (event as any).createdByEmail || '';
      if (!email) throw new Error('no email');
      await saveToken(token, event.id, (event as any).createdBy, email);
      await sendTokenEmail(email, token, event.information);
      await approveAndSetAwaitingToken(event.id);
      setEvents(prev => prev.filter(e => e.id !== event.id));
      setApprovedEvents(prev => [{ ...event, status: 5 }, ...prev]);
      showFeedback(t('mod.token_sent'), 'success');
    } catch {
      showFeedback(t('mod.error_load'), 'danger');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeactivate = async (eventId: string) => {
    setProcessingId(eventId);
    try {
      await deactivateEvent(eventId);
      const deactivated = approvedEvents.find(e => e.id === eventId);
      setApprovedEvents(prev => prev.filter(e => e.id !== eventId));
      if (deactivated) {
        setEvents(prev => [...prev, { ...deactivated, status: 2, published: false }]);
      }
      showFeedback(t('mod.deactivate_success'), 'success');
    } catch {
      showFeedback(t('mod.error_load'), 'danger');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (eventId: string) => {
    setConfirmDeleteId(null);
    setProcessingId(eventId);
    try {
      await deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      showFeedback(t('mod.delete_success'), 'success');
    } catch {
      showFeedback(t('mod.error_load'), 'danger');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    // La navegación la maneja onAuthStateChanged cuando user=null
  };

  const handleLogoutClick = () => setShowLogoutConfirm(true);

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-GT', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="mod-toolbar">
          <IonButtons slot="start">
            <IonIcon icon={shieldCheckmarkOutline} className="mod-toolbar-icon" />
          </IonButtons>
          <IonTitle>{t('mod.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleLogoutClick}>
              <IonIcon icon={logOutOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        {currentEmail && (
          <IonToolbar className="mod-user-bar">
            <div className="mod-user-info">
              <div className="mod-user-avatar">
                {currentEmail.charAt(0).toUpperCase()}
              </div>
              <div className="mod-user-details">
                <span className="mod-user-email">{currentEmail}</span>
                <span className="mod-user-role">
                  <IonIcon icon={shieldCheckmarkOutline} className="mod-user-role-icon" />
                  Moderador
                </span>
              </div>
            </div>
          </IonToolbar>
        )}
      </IonHeader>

      <IonContent className="mod-content">

        {loading ? (
          <div className="mod-loading">
            <IonSpinner name="crescent" />
            <p>{t('mod.loading')}</p>
          </div>

        ) : !authorized ? (
          <div className="mod-unauthorized">
            <IonIcon icon={shieldCheckmarkOutline} className="mod-unauth-icon" />
            <h2>{t('mod.not_authorized')}</h2>
            <p>{currentEmail}</p>
            <IonButton onClick={handleLogoutClick} fill="outline" color="medium">
              {t('common.cancel')}
            </IonButton>
          </div>

        ) : events.length === 0 && approvedEvents.length === 0 && deletionRequests.length === 0 ? (
          <div className="mod-empty">
            <IonIcon icon={shieldCheckmarkOutline} className="mod-empty-icon" />
            <h2>{t('mod.no_pending')}</h2>
          </div>

        ) : (
          <div className="mod-list">
            <IonAccordionGroup multiple={true} value={['pending', 'approved']}>

              {/* Pendientes */}
              {events.length > 0 && (
                <IonAccordion value="pending" className="mod-accordion">
                  <IonItem slot="header" lines="none" className="mod-accordion-header">
                    <IonLabel className="mod-accordion-label">
                      {t('mod.pending_events', { count: String(events.length) })}
                    </IonLabel>
                  </IonItem>
                  <div slot="content" className="mod-accordion-content">
                    <div className="mod-event-list">
                      {events.map(event => (
                        <button
                          key={event.id}
                          className="mod-event-row"
                          onClick={() => { setDetailEvent(event); setDetailType('pending'); }}
                        >
                          <div className="mod-event-row-thumb">
                            {event.photoItem
                              ? <img src={event.photoItem} alt="" />
                              : <IonIcon icon={calendarOutline} className="mod-event-row-icon" />
                            }
                          </div>
                          <div className="mod-event-row-info">
                            <span className="mod-event-row-title">{event.information}</span>
                            <span className="mod-event-row-meta">📅 {formatDate(event.date)} · {event.schedule_start}</span>
                          </div>
                          <IonBadge color="warning" className="mod-event-row-badge">{t('status.pending_moderation')}</IonBadge>
                          <IonIcon icon={chevronForwardOutline} className="mod-event-row-chevron" />
                        </button>
                      ))}
                    </div>
                  </div>
                </IonAccordion>
              )}

              {/* Aprobados */}
              {approvedEvents.length > 0 && (
                <IonAccordion value="approved" className="mod-accordion">
                  <IonItem slot="header" lines="none" className="mod-accordion-header mod-accordion-header--approved">
                    <IonLabel className="mod-accordion-label">
                      {t('mod.approved_events', { count: String(approvedEvents.length) })}
                    </IonLabel>
                  </IonItem>
                  <div slot="content" className="mod-accordion-content">
                    <div className="mod-event-list">
                      {approvedEvents.map(event => (
                        <button
                          key={event.id}
                          className="mod-event-row"
                          onClick={() => { setDetailEvent(event); setDetailType('approved'); }}
                        >
                          <div className="mod-event-row-thumb">
                            {event.photoItem
                              ? <img src={event.photoItem} alt="" />
                              : <IonIcon icon={calendarOutline} className="mod-event-row-icon" />
                            }
                          </div>
                          <div className="mod-event-row-info">
                            <span className="mod-event-row-title">{event.information}</span>
                            <span className="mod-event-row-meta">📅 {formatDate(event.date)} · {event.schedule_start}</span>
                          </div>
                          <IonBadge color="success" className="mod-event-row-badge">
                            {event.status === 1 ? t('status.published') : t('status.approved')}
                          </IonBadge>
                          <IonIcon icon={chevronForwardOutline} className="mod-event-row-chevron" />
                        </button>
                      ))}
                    </div>
                  </div>
                </IonAccordion>
              )}

            </IonAccordionGroup>

            {deletionRequests.length > 0 && (
              <>
                <p className="mod-list-header" style={{ marginTop: events.length > 0 ? 32 : 0 }}>
                  {t('mod.deletion.pending_count', { count: String(deletionRequests.length) })}
                </p>
                <IonAccordionGroup>
                  {deletionRequests.map(req => {
                    const isProcessing = processingId === req.id;
                    const projectId = req.language === 'pt' ? 'anapp44-f2cb1' : 'anapp44-f2cb1';
                    const authConsoleUrl = `https://console.firebase.google.com/project/${projectId}/authentication/users`;
                    const activitiesConsoleUrl = `https://console.firebase.google.com/project/${projectId}/firestore/data/~2Factivities`;
                    return (
                      <IonAccordion key={req.id} value={req.id}>
                        <IonItem slot="header" className="mod-acc-header-item">
                          <div className="mod-acc-header">
                            <span className="mod-acc-title">
                              <IonIcon icon={personRemoveOutline} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                              {req.email}
                            </span>
                            <div className="mod-acc-meta">
                              <span className="mod-acc-date">📅 {formatDate(req.requestedAt)}</span>
                              <IonBadge color="danger">{t('mod.deletion.badge')}</IonBadge>
                            </div>
                          </div>
                        </IonItem>
                        <div slot="content" className="mod-acc-content">
                          <div className="mod-acc-info">
                            {req.reason && (
                              <div className="mod-acc-info-row">
                                <span>💬</span>
                                <span>{req.reason}</span>
                              </div>
                            )}
                            {req.language && (
                              <div className="mod-acc-info-row">
                                <span>🌐</span>
                                <span>{req.language.toUpperCase()}</span>
                              </div>
                            )}
                            <p className="mod-acc-desc" style={{ marginTop: 12 }}>
                              {t('mod.deletion.instructions')}
                            </p>
                          </div>

                          <div className="mod-acc-actions">
                            <IonButton
                              expand="block"
                              fill="outline"
                              href={`mailto:${req.email}?subject=${encodeURIComponent(t('mod.deletion.mail_subject'))}`}
                              target="_blank"
                              className="mod-acc-btn"
                            >
                              <IonIcon icon={mailOutline} slot="start" />
                              {t('mod.deletion.contact_user')}
                            </IonButton>
                            <IonButton
                              expand="block"
                              fill="outline"
                              href={authConsoleUrl}
                              target="_blank"
                              className="mod-acc-btn"
                            >
                              <IonIcon icon={openOutline} slot="start" />
                              {t('mod.deletion.open_auth_console')}
                            </IonButton>
                            <IonButton
                              expand="block"
                              fill="outline"
                              href={activitiesConsoleUrl}
                              target="_blank"
                              className="mod-acc-btn"
                            >
                              <IonIcon icon={openOutline} slot="start" />
                              {t('mod.deletion.open_activities_console')}
                            </IonButton>
                            <IonButton
                              expand="block"
                              color="success"
                              onClick={() => setConfirmDeletionCompleteId(req.id)}
                              disabled={isProcessing}
                              className="mod-acc-btn"
                            >
                              {isProcessing
                                ? <IonSpinner name="crescent" />
                                : <><IonIcon icon={checkmarkCircle} slot="start" />{t('mod.deletion.mark_completed')}</>
                              }
                            </IonButton>
                            <IonButton
                              expand="block"
                              color="medium"
                              fill="outline"
                              onClick={() => setConfirmDeletionRejectId(req.id)}
                              disabled={isProcessing}
                              className="mod-acc-btn"
                            >
                              <IonIcon icon={closeOutline} slot="start" />
                              {t('mod.deletion.reject')}
                            </IonButton>
                          </div>
                        </div>
                      </IonAccordion>
                    );
                  })}
                </IonAccordionGroup>
              </>
            )}
          </div>
        )}

        {/* ── Confirmar procesado de baja ── */}
        <IonAlert
          isOpen={!!confirmDeletionCompleteId}
          onDidDismiss={() => setConfirmDeletionCompleteId(null)}
          header={t('mod.deletion.confirm_completed_title')}
          message={t('mod.deletion.confirm_completed_msg')}
          buttons={[
            { text: t('common.cancel'), role: 'cancel' },
            {
              text: t('mod.deletion.mark_completed'),
              handler: () => { if (confirmDeletionCompleteId) handleCompleteDeletion(confirmDeletionCompleteId); }
            }
          ]}
        />

        {/* ── Confirmar rechazo de baja ── */}
        <IonAlert
          isOpen={!!confirmDeletionRejectId}
          onDidDismiss={() => setConfirmDeletionRejectId(null)}
          header={t('mod.deletion.confirm_rejected_title')}
          message={t('mod.deletion.confirm_rejected_msg')}
          buttons={[
            { text: t('common.cancel'), role: 'cancel' },
            {
              text: t('mod.deletion.reject'),
              role: 'destructive',
              handler: () => { if (confirmDeletionRejectId) handleRejectDeletion(confirmDeletionRejectId); }
            }
          ]}
        />

        {/* ── Lightbox de medios ── */}
        <IonModal
          isOpen={!!selectedMedia}
          onDidDismiss={() => setSelectedMedia(null)}
          className="mod-lightbox-modal"
        >
          <div className="mod-lightbox-inner">
            <button
              className="mod-lightbox-close"
              onClick={() => setSelectedMedia(null)}
            >
              <IonIcon icon={closeOutline} />
            </button>
            {selectedMedia?.type === 'image' && (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.label}
                className="mod-lightbox-img"
              />
            )}
            {selectedMedia?.type === 'video' && (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                playsInline
                className="mod-lightbox-video"
              />
            )}
            {selectedMedia && (
              <p className="mod-lightbox-label">{selectedMedia.label}</p>
            )}
          </div>
        </IonModal>

        {/* ── Confirmación de eliminación ── */}
        <IonAlert
          isOpen={!!confirmDeleteId}
          onDidDismiss={() => setConfirmDeleteId(null)}
          header={t('mod.delete_confirm')}
          buttons={[
            { text: t('common.cancel'), role: 'cancel' },
            {
              text: t('common.delete'),
              role: 'destructive',
              handler: () => {
                if (confirmDeleteId) handleDelete(confirmDeleteId);
              }
            }
          ]}
        />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
          position="top"
        />

      </IonContent>

      {/* Modal mapa */}
      <IonModal isOpen={!!mapCoords} onDidDismiss={() => setMapCoords(null)} className="mod-map-modal">
        <IonHeader>
          <IonToolbar>
            <IonTitle style={{ fontSize: 15 }}>{mapCoords?.label}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setMapCoords(null)}>✕</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {mapCoords && (
            <iframe
              title="mapa"
              width="100%"
              height="100%"
              style={{ border: 0, display: 'block', height: 'calc(100vh - 56px)' }}
              src={`https://www.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}&z=16&output=embed`}
              allowFullScreen
            />
          )}
        </IonContent>
      </IonModal>

      {/* Modal de detalle del evento */}
      <IonModal isOpen={!!detailEvent} onDidDismiss={() => setDetailEvent(null)}>
        {detailEvent && (() => {
          const ev = detailEvent;
          const isProcessing = processingId === ev.id;
          const mediaItems: MediaItem[] = [
            ...(ev.photoItem   ? [{ type: 'image' as const, url: ev.photoItem,   label: t('mod.photo_horizontal') }] : []),
            ...(ev.photoPortrait ? [{ type: 'image' as const, url: ev.photoPortrait, label: t('mod.photo_portrait') }] : []),
            ...(ev.videoUrl    ? [{ type: 'video' as const, url: ev.videoUrl,    label: t('mod.video') }] : []),
          ];
          return (
            <>
              <IonHeader>
                <IonToolbar>
                  <IonButtons slot="start">
                    <IonButton onClick={() => setDetailEvent(null)}>
                      <IonIcon icon={closeOutline} />
                    </IonButton>
                  </IonButtons>
                  <IonTitle style={{ fontSize: 15 }}>{ev.information}</IonTitle>
                  <IonButtons slot="end">
                    <IonBadge color={detailType === 'approved' ? 'success' : 'warning'} style={{ marginRight: 12 }}>
                      {detailType === 'approved'
                        ? (ev.status === 1 ? t('status.published') : t('status.approved'))
                        : t('status.pending_moderation')}
                    </IonBadge>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent>
                <div className="mod-detail-content">

                  {/* Medios */}
                  {mediaItems.length > 0 && (
                    <div className="mod-media-row">
                      {mediaItems.map((media, idx) => (
                        <button key={idx} className="mod-media-item" onClick={() => setSelectedMedia(media)}>
                          {media.type === 'image' ? (
                            <img src={media.url} alt={media.label} className="mod-media-thumb" />
                          ) : (
                            <div className="mod-media-video-wrap">
                              <video src={media.url} className="mod-media-thumb" muted playsInline />
                              <IonIcon icon={playCircleOutline} className="mod-media-play-icon" />
                            </div>
                          )}
                          <span className="mod-media-label">{media.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Info */}
                  <div className="mod-acc-info">
                    <div className="mod-acc-info-row"><span>🕐</span><span>{ev.schedule_start} – {ev.schedule_finish}</span></div>
                    <div className="mod-acc-info-row">
                      <span>📅</span><span>{formatDate(ev.date)}</span>
                    </div>
                    <div className="mod-acc-info-row">
                      <span>📍</span><span>{ev.address_id}</span>
                      {ev.lat && ev.long && (
                        <button className="mod-map-link" onClick={() => setMapCoords({ lat: ev.lat, lng: ev.long, label: ev.address_id })}>
                          <IonIcon icon={locationOutline} />
                        </button>
                      )}
                    </div>
                    <div className="mod-acc-info-row"><span>💰</span><span>{ev.currency ?? ''}{ev.price}</span></div>
                    {ev.information2 && <p className="mod-acc-desc">{ev.information2}</p>}
                  </div>

                  {/* Botones según tipo */}
                  <div className="mod-acc-actions" style={{ padding: '0 16px 24px' }}>
                    {detailType === 'pending' ? (
                      <>
                        <IonButton expand="block" color="success" disabled={isProcessing} className="mod-acc-btn"
                          onClick={() => { handleApprove(ev.id); setDetailEvent(null); }}>
                          {isProcessing ? <IonSpinner name="crescent" /> : <><IonIcon icon={checkmarkCircle} slot="start" />{t('mod.approve')}</>}
                        </IonButton>
                        <IonButton expand="block" color="danger" fill="outline" disabled={isProcessing} className="mod-acc-btn"
                          onClick={() => { setDetailEvent(null); setConfirmDeleteId(ev.id); }}>
                          <IonIcon icon={trashOutline} slot="start" />{t('mod.delete')}
                        </IonButton>
                      </>
                    ) : (
                      <>
                        {/* Si está en status 5 (atascado del flujo anterior), ofrecer publicar */}
                        {ev.status === 5 && (
                          <IonButton expand="block" color="success" disabled={isProcessing} className="mod-acc-btn"
                            onClick={() => { handleApprove(ev.id); setDetailEvent(null); }}>
                            {isProcessing ? <IonSpinner name="crescent" /> : <><IonIcon icon={checkmarkCircle} slot="start" />{t('mod.publish')}</>}
                          </IonButton>
                        )}
                        <IonButton expand="block" color="warning" fill="outline" disabled={isProcessing} className="mod-acc-btn"
                          onClick={() => { handleDeactivate(ev.id); setDetailEvent(null); }}>
                          {isProcessing ? <IonSpinner name="crescent" /> : <><IonIcon icon={closeCircle} slot="start" />{t('mod.deactivate')}</>}
                        </IonButton>
                      </>
                    )}
                  </div>

                </div>
              </IonContent>
            </>
          );
        })()}
      </IonModal>

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title={t('common.logout_confirm_title')}
        message={t('common.logout_confirm_msg')}
        confirmLabel={t('common.logout')}
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        onConfirm={handleSignOut}
        onCancel={() => setShowLogoutConfirm(false)}
      />

    </IonPage>
  );
};

export default ModeratorPage;
